<template>
  <div
    :class="{
      [$style.card]: true,
      [$style[variant || '']]: variant,
      [$style.border]: border,
      [$style.noPadding]: noPadding,
    }"
    :role="role || (onClick ? 'button' : undefined)"
    @click="handleClick"
  >
    <slot />
  </div>
</template>
<script setup lang="ts">
import { useTrack } from '@/composables/plugins.js';

type Props = {
  variant?:
    | 'light'
    | 'dark'
    | 'darker'
    | 'transparent'
    | 'warning'
    | 'info'
    | 'alert';
  onClick?: (e: Event) => void;
  role?: string;
  tid?: string;
  border?: boolean;
  noPadding?: boolean;
};

const track = useTrack();
const props = defineProps<Props>();

const trackClick = () => {
  if (!props.tid) return;
  track('card-click', { tid: props.tid });
};

const handleClick = (event: Event) => {
  if (!props.onClick) return;
  trackClick();
  props.onClick(event);
};
</script>

<style module>
.card {
  padding: 1rem;
  border-radius: 0.75rem;
  background: var(--neutral-100);
  border: 1px solid transparent;
  transition: all 0.2s ease-in-out;
}

.card[role='button'] {
  cursor: pointer;
}

.card[role='button']:hover {
  border-color: var(--amber-600);
}

.card > .card {
  margin: 1rem 0 0;
}

.border {
  border: 1px solid var(--neutral-400);
}

.light {
  background: var(--neutral-50);
}

.dark {
  background: var(--neutral-200);
}

.darker {
  background: var(--neutral-300);
}

.warning {
  background: var(--amber-100);

  &.border {
    border-color: var(--amber-300);
  }
}

.alert {
  background: var(--red-100);

  &.border {
    border-color: var(--red-300);
  }
}

.card.alert[role='button']:hover {
  background: var(--neutral-50);

  &.border {
    border-color: var(--red-400);
  }
}

.info {
  background: var(--blue-100);

  &.border {
    border-color: var(--blue-300);
  }
}

.transparent {
  background: transparent;
}

.noPadding {
  padding: 0;
}
</style>
